import {track} from './journeyEvents';
import { authService } from './auth';
import { API_BASE_URL } from './api';
import { captureHeaders } from './captureSession';

export interface BodyPoseCapabilities {
  premium: boolean;
  max_upload_bytes: number;
  max_duration_seconds: number;
  segment_max_bytes: number;
  segment_max_seconds: number;
  max_segments: number;
  automatic_resolution: boolean;
}

export interface BodyPoseSegment {
  index: number;
  start_seconds: number;
  end_seconds: number;
  frames?: number;
  fps?: number;
  width?: number;
  height?: number;
}

export interface BodyPoseJob {
  job_id: string;
  filename: string;
  state: string;
  stage: string | null;
  created_at: string;
  updated_at: string;
  result_available: boolean;
  retryable: boolean;
  error: { code: string; message: string } | null;
  metadata: {
    frames?: number; frame_count?: number; fps?: number; joint_count?: number; units?: string;
    result_format?: 'bvh' | 'zip'; segment_count?: number; current_segment?: number;
    segments?: BodyPoseSegment[];
  } | null;
}

export class BodyPoseApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'BodyPoseApiError';
  }
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const token = authService.getToken();
  const downloading=path.endsWith('/result');
  if(!token)throw new BodyPoseApiError('Create an account or sign in before using capture.',401);
  const headers=await captureHeaders(init.headers,downloading);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/body-pose-mocap${path}`, {
      ...init, headers, cache: 'no-store',
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new BodyPoseApiError('The connection was interrupted. Check your capture history before sending the video again.', 0);
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const detail = data?.detail;
    const message = typeof detail === 'string' ? detail : detail?.message;
    throw new BodyPoseApiError(response.status === 401
      ? 'Your session has expired. Sign in again to view your captures.'
      : message || 'The capture service could not complete this request. Please try again.', response.status);
  }
  return response;
}

export const bodyPoseService = {
  async capabilities(signal?: AbortSignal): Promise<BodyPoseCapabilities> {
    return (await request('/capabilities', { signal })).json();
  },
  async list(signal?: AbortSignal): Promise<BodyPoseJob[]> {
    const response = await request('/jobs?limit=50', { signal });
    const data = await response.json();
    return data.jobs;
  },
  async get(jobId: string, signal?: AbortSignal): Promise<BodyPoseJob> {
    return (await request(`/jobs/${encodeURIComponent(jobId)}`, { signal })).json();
  },
  async estimate(video:File,signal?:AbortSignal):Promise<{estimated_credits:number;input_nanoseconds:string}> {const body=new FormData();body.append('video',video);return (await request('/estimate',{method:'POST',body,signal})).json();},
  async cancel(jobId:string,signal?:AbortSignal):Promise<BodyPoseJob>{return (await request(`/jobs/${encodeURIComponent(jobId)}/cancel`,{method:'POST',signal})).json();},
  async submit(video: File, idempotencyKey: string, signal?: AbortSignal, expectedCredits?:number): Promise<BodyPoseJob> {
    track('upload_started',{surface:'body',modality:'BODY_MOCAP'});
    const body = new FormData();
    body.append('video', video);
    body.append('static_camera', 'true');
    try{return (await request('/jobs', {
      method: 'POST', body, signal, headers: { 'Idempotency-Key': idempotencyKey,...(expectedCredits!==undefined ? {'X-Expected-Credits':String(expectedCredits)} : {}) },
    })).json();}catch(error){track('upload_failed',{surface:'body',error_code:error instanceof BodyPoseApiError && error.status===422?'video_invalid':'network'});throw error;}
  },
  async download(jobId: string, signal?: AbortSignal): Promise<Blob> {
    track('download_attempted',{surface:'body',job_id:jobId});
    const blob=await (await request(`/jobs/${encodeURIComponent(jobId)}/result`, { signal })).blob();
    track('file_received',{surface:'body',job_id:jobId});return blob;
  },
};
